import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";

export const AuthCallback: React.FC = () => {
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check if we have an error in the URL
        const urlParams = new URLSearchParams(window.location.search);
        const errorParam = urlParams.get("error");
        const tokenParam = urlParams.get("token");
        
        if (errorParam) {
          setError(decodeURIComponent(errorParam));
          setStatus("error");
          return;
        }

        // If we have a token from the OAuth callback, store it (production only)
        if (tokenParam) {
          const isDevelopment = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
          if (!isDevelopment) {
            localStorage.setItem('auth_token', tokenParam);
            console.log("Token stored in localStorage for production");
          } else {
            console.log("Development mode - using cookies for authentication");
          }
        }

        // Check authentication status
        await checkAuth();
        setStatus("success");
        
        // Redirect to app after a short delay
        setTimeout(() => {
          window.location.href = "/app";
        }, 1500);
      } catch (err) {
        console.error("Auth callback error:", err);
        setError("Authentication failed. Please try again.");
        setStatus("error");
      }
    };

    handleCallback();
  }, [checkAuth]);

  if (status === "loading") {
    return (
      <div className="h-screen bg-gradient-to-br from-bg-primary to-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-6" />
          <h2 className="text-2xl font-semibold text-white mb-2">Completing Sign In</h2>
          <p className="text-gray-300">Please wait while we verify your authentication...</p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="h-screen bg-gradient-to-br from-bg-primary to-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-white mb-2">Sign In Successful!</h2>
          <p className="text-gray-300 mb-6">Redirecting you to the application...</p>
          <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full mx-auto" />
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="h-screen bg-gradient-to-br from-bg-primary to-bg-secondary flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-white mb-2">Sign In Failed</h2>
          <p className="text-gray-300 mb-6">
            {error || "An error occurred during authentication. Please try again."}
          </p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = "/login"}
              className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.href = "/"}
              className="w-full px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
